import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LogOut, Wrench, Search, Users, DollarSign, TrendingUp, AlertCircle, Ticket } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US"); } catch { return iso; }
}
function fmtMoney(n) { return `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function AdminOverview() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const [o, c] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/companies", { params: statusFilter === "all" ? {} : { status: statusFilter } }),
    ]);
    setOverview(o.data); setCompanies(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleLogout = () => { logout(); nav("/login"); };

  const filtered = (companies || []).filter((c) =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.owner_email.toLowerCase().includes(q.toLowerCase())
  );

  const maxRev = Math.max(1, ...(overview?.revenue_by_month || []).map((m) => m.revenue));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-slate-900 text-white px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center"><Wrench className="w-4 h-4" /></div>
          <div className="font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ · Admin</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-400 hidden sm:inline">{user?.email}</span>
          <Link to="/admin/coupons" data-testid="nav-coupons">
            <Button variant="outline" className="h-9 bg-transparent text-white border-slate-700 hover:bg-slate-800 rounded-lg">
              <Ticket className="w-4 h-4 mr-1" /> Coupons
            </Button>
          </Link>
          <ChangePasswordDialog />
          <Button onClick={handleLogout} variant="outline" className="h-9 bg-transparent text-white border-slate-700 hover:bg-slate-800 rounded-lg" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        <h1 className="text-3xl font-bold">Founder Dashboard</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total Companies" value={overview?.total_companies ?? "—"} icon={Users} color="bg-slate-100 text-slate-700" testId="kpi-total" />
          <Kpi label="Free Trials" value={overview?.active_trials ?? "—"} icon={AlertCircle} color="bg-blue-100 text-blue-700" testId="kpi-trials" />
          <Kpi label="Paying" value={overview?.paying_subscribers ?? "—"} icon={TrendingUp} color="bg-green-100 text-green-700" testId="kpi-paying" />
          <Kpi label="MRR" value={overview ? fmtMoney(overview.mrr_usd) : "—"} icon={DollarSign} color="bg-emerald-100 text-emerald-700" testId="kpi-mrr" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Revenue by month</h2>
              <div className="text-sm text-slate-500">Total collected: <b className="text-slate-900">{overview ? fmtMoney(overview.total_revenue_usd) : "—"}</b></div>
            </div>
            <div className="flex items-end gap-1.5 h-40" data-testid="revenue-chart">
              {(overview?.revenue_by_month || []).map((m) => (
                <div key={m.key} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.label}: ${fmtMoney(m.revenue)}`}>
                  <div className="w-full bg-blue-600 rounded-t" style={{ height: `${Math.max(4, (m.revenue / maxRev) * 100)}%` }} />
                  <div className="text-[10px] text-slate-500 truncate w-full text-center">{m.label.split(" ")[0]}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Snapshot</h2>
            <SnapRow label="Expired / churned" value={overview?.expired ?? "—"} testId="snap-expired" />
            <SnapRow label="Plan price" value={overview ? fmtMoney(overview.price_usd) : "—"} />
            <SnapRow label="Total revenue" value={overview ? fmtMoney(overview.total_revenue_usd) : "—"} testId="snap-revenue" />
            <RemindersMini />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Companies</h2>
            <div className="flex gap-2 flex-wrap">
              {["all","trial","active","expired"].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
                  className={`h-9 px-3 rounded-full text-xs font-bold uppercase tracking-wide ${statusFilter===s?"bg-slate-900 text-white":"bg-slate-100 text-slate-700"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or email…" className="h-11 pl-10" data-testid="admin-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2">Company</th>
                  <th className="text-left py-2 px-2">Owner</th>
                  <th className="text-left py-2 px-2">Signed up</th>
                  <th className="text-left py-2 px-2">Trial ends</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Users</th>
                  <th className="text-right py-2 px-2">Jobs</th>
                  <th className="text-right py-2 px-2">Invoices</th>
                </tr>
              </thead>
              <tbody>
                {(filtered || []).map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/admin/companies/${c.id}`)} data-testid={`admin-row-${c.id}`}>
                    <td className="py-2 px-2 font-semibold">{c.name}</td>
                    <td className="py-2 px-2 text-slate-600">{c.owner_name}<div className="text-xs text-slate-400">{c.owner_email}</div></td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(c.created_at)}</td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(c.trial_ends_at)}</td>
                    <td className="py-2 px-2"><StatusChip s={c.subscription_status} /></td>
                    <td className="py-2 px-2 text-right">{c.users}</td>
                    <td className="py-2 px-2 text-right">{c.jobs}</td>
                    <td className="py-2 px-2 text-right">{c.invoices}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-slate-500">No companies match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
}

function SnapRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-sm font-bold" data-testid={testId}>{value}</div>
    </div>
  );
}

function StatusChip({ s }) {
  const cls = s === "active" ? "bg-green-100 text-green-800" : s === "expired" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${cls}`}>{s}</span>;
}

function RemindersMini() {
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/reminders/preview");
      setPreview(data);
    } catch { /* keep silent */ }
  };
  useEffect(() => { load(); }, []);
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/reminders/run");
      // toast is imported lazily to avoid unused-import lint if scoped elsewhere
      const { toast } = await import("sonner");
      toast.success(`Reminders sent: ${data.sent} (scanned ${data.processed})`);
      await load();
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Reminder run failed");
    } finally { setRunning(false); }
  };
  const due = preview?.would_send?.length ?? 0;
  return (
    <div className="mt-2 rounded-xl border border-slate-200 p-3" data-testid="reminders-mini">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Unpaid reminders</div>
        <div className="text-xs text-slate-400">{preview?.processed ?? 0} unpaid open</div>
      </div>
      <div className="text-sm text-slate-700 mb-2" data-testid="reminders-due-count">
        {due === 0 ? "No new reminders due right now." : <><b>{due}</b> invoice{due === 1 ? "" : "s"} eligible (3 / 7 / 14 day stages).</>}
      </div>
      <button type="button" onClick={run} disabled={running || due === 0}
        className="w-full h-9 rounded-lg text-xs font-bold uppercase tracking-wide bg-amber-500 hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 text-white transition"
        data-testid="reminders-run">
        {running ? "Running…" : "Run reminders now"}
      </button>
    </div>
  );
}
